/**
 * useIsOverflowing Hook
 *
 * Detects whether an element's content is overflowing (text is truncated).
 * Works with single-line truncation (text-overflow: ellipsis) and
 * multi-line truncation (-webkit-line-clamp).
 *
 * @returns A ref to attach to the element and a boolean indicating if content overflows
 */

import { useState, useEffect, useRef, RefObject, useCallback } from 'react';

/**
 * Check if an element's text content is overflowing/truncated
 *
 * @returns [ref, isOverflowing] - Ref to attach to element and overflow state
 */
export function useIsOverflowing<T extends HTMLElement>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    const element = ref.current;
    if (!element) {
      setIsOverflowing(false);
      return;
    }

    // For multi-line truncation (line-clamp), scrollHeight > clientHeight
    // For single-line truncation, scrollWidth > clientWidth
    const hasVerticalOverflow = element.scrollHeight > element.clientHeight;
    const hasHorizontalOverflow = element.scrollWidth > element.clientWidth;

    setIsOverflowing(hasVerticalOverflow || hasHorizontalOverflow);
  }, []);

  useEffect(() => {
    checkOverflow();

    // Re-check on window resize as container size may change
    window.addEventListener('resize', checkOverflow);

    // Use ResizeObserver if available for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(ref.current);
    }

    return () => {
      window.removeEventListener('resize', checkOverflow);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [checkOverflow]);

  return [ref, isOverflowing];
}

export default useIsOverflowing;
